import { computed, ref, type MaybeRefOrGetter, toValue } from "vue";
import { useRouter } from "vue-router";

import type { ModeTransition } from "@/components/modals/EditorModeConfirmation.vue";
import { markdownContainsComments } from "@/lib/editor/markdown/utils";
import { useFeaturePermitted } from "@/store/stores/account";
import type { EditorMode } from "@/types/email";

interface UseEditorModeSwitchOptions {
  mode: MaybeRefOrGetter<EditorMode | undefined>;
  body: MaybeRefOrGetter<string | undefined>;
  onModeChange: (mode: EditorMode) => void;
  onSwitchToNakedMode: (body: string) => void;
  onClearBodyForModeTransition: () => void;
}

export function useEditorModeSwitch(options: UseEditorModeSwitchOptions) {
  const router = useRouter();

  const isSwitchingToNakedMode = ref(false);
  const modeTransition = ref<ModeTransition>();

  const canUseNakedMode = useFeaturePermitted("naked_mode");

  const currentMode = computed(() => toValue(options.mode));
  const currentBody = computed(() => toValue(options.body));

  const switchMode = (next: EditorMode) => {
    const prev = currentMode.value;

    if (prev === undefined || prev === next) {
      return;
    }

    if (next === "naked") {
      if (!canUseNakedMode.value) {
        router.push({ name: "/settings/billing" });
        return;
      }

      isSwitchingToNakedMode.value = true;
      return;
    } else if (prev === "naked") {
      modeTransition.value = { from: prev, to: next, detail: "clear_body" };
      return;
    }

    if (prev === "plaintext" && next === "fancy") {
      const body = currentBody.value;
      if (body && markdownContainsComments(body)) {
        modeTransition.value = {
          from: prev,
          to: next,
          detail: "html_comments",
        };
        return;
      }
    }

    options.onModeChange(next);
  };

  const handleSwitchToNakedMode = (incomingBody: string) => {
    isSwitchingToNakedMode.value = false;
    options.onSwitchToNakedMode(incomingBody);
  };

  const handleModeTransition = (tx: ModeTransition) => {
    if (tx.from === "naked") {
      options.onClearBodyForModeTransition();
    }

    modeTransition.value = undefined;
    options.onModeChange(tx.to);
  };

  const closeNakedModeConfirmation = () => {
    isSwitchingToNakedMode.value = false;
  };

  const closeModeTransitionConfirmation = () => {
    modeTransition.value = undefined;
  };

  return {
    isSwitchingToNakedMode,
    modeTransition,
    switchMode,
    handleSwitchToNakedMode,
    handleModeTransition,
    closeNakedModeConfirmation,
    closeModeTransitionConfirmation,
  };
}
